const CART_DRAWER_SECTION_ID = 'cart-drawer-section';
const CART_PAGE_SECTION_ID = 'cart-section';

async function fetchCart() {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`Cart fetch failed: ${response.status}`);
  }

  return response.json();
}

function updateCartBubble(cart) {
  const cartIcon = document.querySelector('cart-icon');
  if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
    const itemCount = typeof cart === 'number' ? cart : getDisplayItemCount(cart);
    cartIcon.renderCartBubble(itemCount);
  }
}

function getProtectionVariantId() {
  const protection = document.querySelector('[data-comb-protection]');
  if (!(protection instanceof HTMLElement)) return 0;

  return Number(protection.dataset.variantId || 0);
}

function getProtectionElement() {
  const protection = document.querySelector('[data-comb-protection]');
  return protection instanceof HTMLElement ? protection : null;
}

function getProtectionToggle(protection) {
  const toggle = protection?.querySelector('[data-comb-protection-toggle]');
  return toggle instanceof HTMLButtonElement ? toggle : null;
}

function isProtectionEnabled(protection) {
  const toggle = getProtectionToggle(protection);
  return toggle?.getAttribute('aria-pressed') === 'true';
}

function getDisplayItemCount(cart) {
  const itemCount = Number(cart?.item_count ?? 0);
  const protectionVariantId = getProtectionVariantId();
  if (!protectionVariantId || !Array.isArray(cart?.items)) return itemCount;

  const protectionQuantity = cart.items.reduce((sum, item) => {
    return Number(item.variant_id) === protectionVariantId ? sum + Number(item.quantity || 0) : sum;
  }, 0);

  return Math.max(0, itemCount - protectionQuantity);
}

async function postCart(path, payload) {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Cart request failed: ${response.status}`);
  }

  return response.json();
}

async function refreshCartDrawer(sections) {
  if (sections?.[CART_DRAWER_SECTION_ID]) {
    replaceCartDrawer(sections[CART_DRAWER_SECTION_ID]);
    return;
  }

  const sectionsUrl = new URL(getSectionsUrl(), window.location.origin);
  sectionsUrl.searchParams.set('sections', CART_DRAWER_SECTION_ID);

  const response = await fetch(sectionsUrl.toString());
  if (!response.ok) return;

  const fetchedSections = await response.json();
  const markup = fetchedSections[CART_DRAWER_SECTION_ID];
  if (!markup) return;
  replaceCartDrawer(markup);
}

async function refreshCartPage(sections) {
  const cartPageSectionId = getCartPageSectionId();

  if (sections?.[cartPageSectionId]) {
    replaceCartPage(sections[cartPageSectionId]);
    return;
  }

  const sectionsUrl = new URL(getSectionsUrl(), window.location.origin);
  sectionsUrl.searchParams.set('sections', cartPageSectionId);

  const response = await fetch(sectionsUrl.toString());
  if (!response.ok) return;

  const fetchedSections = await response.json();
  const markup = fetchedSections[cartPageSectionId];
  if (!markup) return;
  replaceCartPage(markup);
}

function replaceCartPage(markup) {
  const nextDocument = new DOMParser().parseFromString(markup, 'text/html');
  const nextComponent = nextDocument.querySelector('cart-items-component');
  const currentComponent = document.querySelector('cart-items-component:not([data-drawer])');

  if (nextComponent && currentComponent) {
    currentComponent.replaceWith(nextComponent);
  }

  document.dispatchEvent(new CustomEvent('cart:refresh'));
}

function replaceCartDrawer(markup) {
  const nextDocument = new DOMParser().parseFromString(markup, 'text/html');
  const nextInner = nextDocument.querySelector('[data-hydration-key="cart-drawer-inner"]');
  const currentInner = document.querySelector('#cart-drawer [data-hydration-key="cart-drawer-inner"]');

  if (nextInner && currentInner) {
    currentInner.replaceWith(nextInner);
  }

  document.dispatchEvent(new CustomEvent('cart:refresh'));
}

function setProtectionLoading(protection, isLoading) {
  const totals = protection.closest('.cart-totals');
  protection.classList.toggle('comb-cart-protection--updating', isLoading);
  protection.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  totals?.classList.toggle('cart-totals--protection-updating', isLoading);
}

function getSectionsUrl() {
  return `${window.location.pathname}${window.location.search}`;
}

function isCartPage() {
  return window.location.pathname.replace(/\/$/, '').endsWith('/cart');
}

function getCartPageSectionId() {
  const cartPageComponent = document.querySelector('cart-items-component:not([data-drawer])');
  return cartPageComponent?.dataset?.sectionId || CART_PAGE_SECTION_ID;
}

function getMutationPayload(enabled, line, variantId) {
  const cartPageSectionId = getCartPageSectionId();
  const basePayload = {
    sections: isCartPage() ? `${cartPageSectionId},${CART_DRAWER_SECTION_ID}` : CART_DRAWER_SECTION_ID,
    sections_url: getSectionsUrl(),
  };

  const updates = {};
  updates[variantId] = enabled ? 0 : 1;
  return {
    path: 'cart/update.js',
    payload: { ...basePayload, updates },
  };
}

async function normalizeProtectionQuantity() {
  const protection = getProtectionElement();
  if (!protection) return;

  const toggle = getProtectionToggle(protection);
  const variantId = Number(protection.dataset.variantId);
  const quantity = Number(protection.dataset.quantity || 0);
  const enabled = isProtectionEnabled(protection);

  if (!enabled || !variantId || quantity <= 1 || protection.classList.contains('comb-cart-protection--updating')) return;

  const cartPageSectionId = getCartPageSectionId();
  const updates = {};
  updates[variantId] = 1;

  setProtectionLoading(protection, true);
  if (toggle instanceof HTMLButtonElement) toggle.disabled = true;

  try {
    const result = await postCart('cart/update.js', {
      sections: isCartPage() ? `${cartPageSectionId},${CART_DRAWER_SECTION_ID}` : CART_DRAWER_SECTION_ID,
      sections_url: getSectionsUrl(),
      updates,
    });
    if (isCartPage()) {
      await refreshCartPage(result.sections);
    }
    await refreshCartDrawer(result.sections);
    updateCartBubble(result);
  } catch (error) {
    console.error(error);
  } finally {
    if (protection.isConnected) {
      setProtectionLoading(protection, false);
      if (toggle instanceof HTMLButtonElement) toggle.disabled = false;
    }
  }
}

async function syncProtectionForCheckout() {
  const protection = getProtectionElement();
  if (!protection) return;

  const variantId = Number(protection.dataset.variantId);
  if (!variantId) return;

  const enabled = isProtectionEnabled(protection);
  const quantity = Number(protection.dataset.quantity || 0);
  const desiredQuantity = enabled ? 1 : 0;
  if (quantity === desiredQuantity) return;

  const cartPageSectionId = getCartPageSectionId();
  const updates = {};
  updates[variantId] = desiredQuantity;

  await postCart('cart/update.js', {
    sections: isCartPage() ? `${cartPageSectionId},${CART_DRAWER_SECTION_ID}` : CART_DRAWER_SECTION_ID,
    sections_url: getSectionsUrl(),
    updates,
  });
}

function submitCheckoutForm(form) {
  const checkoutInput = document.createElement('input');
  checkoutInput.type = 'hidden';
  checkoutInput.name = 'checkout';
  checkoutInput.value = 'checkout';
  form.append(checkoutInput);
  HTMLFormElement.prototype.submit.call(form);
}

document.addEventListener('click', async (event) => {
  if (!(event.target instanceof Element)) return;

  const toggle = event.target.closest('[data-comb-protection-toggle]');
  if (!(toggle instanceof HTMLButtonElement)) return;

  const protection = toggle.closest('[data-comb-protection]');
  if (!(protection instanceof HTMLElement)) return;
  if (protection.classList.contains('comb-cart-protection--updating')) return;

  const variantId = Number(protection.dataset.variantId);
  const line = Number(protection.dataset.line);
  const enabled = toggle.getAttribute('aria-pressed') === 'true';
  const nextEnabled = !enabled;
  const previousLine = protection.dataset.line || '0';

  if (!variantId && !enabled) return;

  toggle.setAttribute('aria-pressed', nextEnabled ? 'true' : 'false');
  protection.dataset.line = nextEnabled ? previousLine : '0';
  setProtectionLoading(protection, true);
  toggle.disabled = true;

  try {
    const { path, payload } = getMutationPayload(enabled, line, variantId);
    const result = await postCart(path, payload);
    if (isCartPage()) {
      await refreshCartPage(result.sections);
    }
    await refreshCartDrawer(result.sections);

    const cart = result.item_count !== undefined ? result : await fetchCart();
    updateCartBubble(cart);
  } catch (error) {
    // Keep the current drawer usable if Shopify rejects the cart update.
    toggle.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    protection.dataset.line = previousLine;
    console.error(error);
  } finally {
    if (protection.isConnected) {
      setProtectionLoading(protection, false);
      toggle.disabled = false;
    }
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const submitter = event.submitter;
  const isCheckoutSubmit =
    form.classList.contains('comb-cart-checkout-form') ||
    (submitter instanceof HTMLElement && submitter.getAttribute('name') === 'checkout');

  if (!isCheckoutSubmit || form.dataset.combProtectionCheckoutSynced === 'true') return;
  if (!getProtectionElement()) return;

  event.preventDefault();
  form.dataset.combProtectionCheckoutSynced = 'true';
  if (submitter instanceof HTMLButtonElement) submitter.disabled = true;

  try {
    await syncProtectionForCheckout();
    submitCheckoutForm(form);
  } catch (error) {
    delete form.dataset.combProtectionCheckoutSynced;
    if (submitter instanceof HTMLButtonElement) submitter.disabled = false;
    console.error(error);
  }
});

normalizeProtectionQuantity();
document.addEventListener('cart:refresh', normalizeProtectionQuantity);
